import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  industries,
  teamSizes,
  primaryPains,
  budgetOptions,
  readinessOptions,
  countryCodes,
  getRecommendedTools,
  getAllTools,
} from './data/tools';
import {
  fetchAvailableSlots,
  trackProgress, 
  submitToGoogleSheets, 
  fetchCalendarSlots, 
  sendWhatsAppTextMessage,
  getNextSevenDays,
} from './utils/api';
import config from './config';
import './styles.css';


// Screen Components
const ProgressBar = ({ currentStep, totalSteps }) => (
  <div className="progress-container">
    <div className="progress-text">
      <span>Step {currentStep} of {totalSteps}</span>
      <span>{Math.round((currentStep / totalSteps) * 100)}%</span>
    </div>
    <div className="progress-bar">
      <div 
        className="progress-fill" 
        style={{ width: `${(currentStep / totalSteps) * 100}%` }}
      />
    </div>
  </div>
);

const OptionCard = ({ option, selected, onClick, showInput, inputValue, onInputChange }) => (
  <div>
    <div 
      className={`option-card ${selected ? 'selected' : ''}`}
      onClick={() => onClick(option.id)}
    >
      <span className="option-icon">{option.icon}</span>
      <span className="option-label">{option.label.replace(option.icon, '').trim()}</span>
      <div className="option-check">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
    </div>
    {option.hasInput && selected && (
      <motion.div 
        className="other-input-container"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
      >
        <input
          type="text"
          className="other-input"
          placeholder="Please specify..."
          value={inputValue || ''}
          onChange={(e) => onInputChange(e.target.value)}
          autoFocus
        />
      </motion.div>
    )}
  </div>
);

const ToolCard = ({ tool, selected, onClick, showBadge }) => (
  <div 
    className={`tool-card ${selected ? 'selected' : ''}`}
    onClick={() => onClick(tool.id)}
  >
    <div className="tool-header">
      <span className="tool-name">{tool.name}</span>
    </div>
    <p className="tool-description">{tool.description}</p>
  </div>
);

// Generate unique session ID
const generateSessionId = () => {
  return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// URL validation utility
const isValidUrl = (string) => {
  if (!string || string.trim().length < 4) return false;
  
  try {
    // Add protocol if missing
    let url = string.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }
    
    const urlObj = new URL(url);
    
    // Check if hostname has at least one dot and valid characters
    const hostname = urlObj.hostname.toLowerCase();
    if (!hostname.includes('.') || hostname.length < 4) {
      return false;
    }
    
    // Basic hostname validation - should contain valid characters
    const hostnameRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return hostnameRegex.test(hostname);
  } catch (_) {
    return false;
  }
};

// Word count utility
const getWordCount = (text) => {
  if (!text || text.trim().length === 0) return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
};

// Main App Component
function App() {
  const [currentStep, setCurrentStep] = useState(1);
  const [sessionId] = useState(() => generateSessionId());
  const [formData, setFormData] = useState({
    startTime: new Date().toISOString(),
    industry: '',
    industryOther: '',
    teamSize: '',
    website: '',
    painPoint: '',
    painPointOther: '',
    businessDescription: '',
    selectedTools: [],
    budget: '',
    readiness: '',
    countryCode: '+91',
    phoneNumber: '',
    name: '',
    email: '',
    businessName: '',
    selectedDate: null,
    selectedSlot: null,
  });
  const [recommendedTools, setRecommendedTools] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const totalSteps = 9;

  // Initialize dates
  useEffect(() => {
    setAvailableDates(getNextSevenDays());
  }, []);

  // Track progress whenever step changes
  useEffect(() => {
    if (currentStep >= 1 && currentStep <= 9) {
      trackProgress(formData, currentStep, sessionId, false);
    }
  }, [currentStep]);

  // Fetch time slots when date is selected
  useEffect(() => {
    if (formData.selectedDate) {
      setIsLoadingSlots(true);
      fetchAvailableSlots(formData.selectedDate)
        .then(slots => {
          setAvailableSlots(slots);
          setIsLoadingSlots(false);
        })
        .catch(() => {
          setIsLoadingSlots(false);
        });
    }
  }, [formData.selectedDate]);

  // Update recommended tools when pain point changes
  useEffect(() => {
    if (formData.industry === 'other' || formData.painPoint === 'other') {
      // Show all tools for "Other" selections
      const tools = getAllTools();
      setRecommendedTools(tools);
      
      // Fire Meta Pixel ViewContent
      if (window.fbq && tools.length > 0) {
        window.fbq('track', 'ViewContent', {
          content_type: 'product',
          content_category: 'all_tools',
          num_items: tools.length,
        });
      }
    } else if (formData.painPoint) {
      const tools = getRecommendedTools(formData.painPoint);
      setRecommendedTools(tools);
      
      // Fire Meta Pixel ViewContent when tools are shown
      if (window.fbq && tools.length > 0) {
        window.fbq('track', 'ViewContent', {
          content_type: 'product',
          content_category: formData.painPoint,
          num_items: tools.length,
        });
      }
    }
  }, [formData.industry, formData.painPoint]);

  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleNext = () => {
    if (currentStep < totalSteps) {
      const nextStep = currentStep + 1;
      
      // Fire Meta Pixel InitiateCheckout when reaching scheduling screen
      if (nextStep === 9 && window.fbq) {
        window.fbq('track', 'InitiateCheckout', {
          content_category: formData.painPoint,
          num_items: formData.selectedTools.length,
          value: formData.budget === 'above_50k' ? 50000 : formData.budget === '15k_50k' ? 15000 : 15000,
          currency: 'INR',
        });
      }
      
      setCurrentStep(nextStep);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Fire Meta Pixel Lead event
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: recommendedTools.find(t => formData.selectedTools.includes(t.id))?.name || 'AI Tool',
          content_category: formData.painPoint,
          value: formData.budget === 'above_50k' ? 50000 : formData.budget === '15k_50k' ? 15000 : 15000,
          currency: 'INR',
        });
      }

      // Submit final data with completed = true
      await submitToGoogleSheets({
        ...formData,
        scheduledDate: formData.selectedDate?.toLocaleDateString('en-IN'),
        scheduledTime: formData.selectedSlot?.displayTime,
        slotStart: formData.selectedSlot?.start,
        slotEnd: formData.selectedSlot?.end,
      }, sessionId);

      // Send WhatsApp confirmation only for scheduled calls, not for WhatsApp sharing option
      if (formData.readiness === 'yes') {
        await sendWhatsAppTextMessage({
          ...formData,
          scheduledDate: formData.selectedDate?.toLocaleDateString('en-IN'),
          scheduledTime: formData.selectedSlot?.displayTime,
          selectedToolName: recommendedTools.find(t => formData.selectedTools.includes(t.id))?.name,
        });
      }

      setCurrentStep(10); // Success screen
    } catch (error) {
      console.error('Submission error:', error);
      setCurrentStep(10); // Still show success (data saved locally)
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.industry && (formData.industry !== 'other' || formData.industryOther);
      case 2: return formData.teamSize;
      case 3: return formData.website && isValidUrl(formData.website);
      case 4: return formData.painPoint && (formData.painPoint !== 'other' || formData.painPointOther);
      case 5: return formData.businessDescription && getWordCount(formData.businessDescription) >= 10;
      case 6: return formData.selectedTools.length > 0;
      case 7: return formData.budget;
      case 8: return formData.readiness;
      case 9: return formData.phoneNumber.length >= 10 && formData.selectedDate && formData.selectedSlot;
      default: return false;
    }
  };

  const renderScreen = () => {
    const pageVariants = {
      initial: { opacity: 0, x: 20 },
      animate: { opacity: 1, x: 0 },
      exit: { opacity: 0, x: -20 }
    };

    switch (currentStep) {
      // Screen 1: Industry
      case 1:
        return (
          <motion.div key="industry" {...pageVariants} className="screen-content">
            <h1 className="question-title">What type of business do you have?</h1>
            <p className="question-subtitle">This helps us recommend the right AI tools for your industry</p>
            <div className="options-grid">
              {industries.map(option => (
                <OptionCard
                  key={option.id}
                  option={option}
                  selected={formData.industry === option.id}
                  onClick={(id) => updateFormData('industry', id)}
                  showInput={option.hasInput && formData.industry === option.id}
                  inputValue={formData.industryOther}
                  onInputChange={(val) => updateFormData('industryOther', val)}
                />
              ))}
            </div>
          </motion.div>
        );

      // Screen 2: Team Size
      case 2:
        return (
          <motion.div key="teamsize" {...pageVariants} className="screen-content">
            <h1 className="question-title">How many people in your team?</h1>
            <p className="question-subtitle">Helps us understand your scale of operations</p>
            <div className="options-grid">
              {teamSizes.map(option => (
                <OptionCard
                  key={option.id}
                  option={option}
                  selected={formData.teamSize === option.id}
                  onClick={(id) => updateFormData('teamSize', id)}
                />
              ))}
            </div>
          </motion.div>
        );

      // Screen 3: Website URL
      case 3:
        return (
          <motion.div key="website" {...pageVariants} className="screen-content">
            <h1 className="question-title">What's your business website?</h1>
            <p className="question-subtitle">We'll analyze your business to provide better recommendations</p>
            <div className="form-field">
              <input
                type="text"
                className="form-input"
                placeholder="Enter your website URL (e.g., www.yourcompany.com)"
                value={formData.website}
                onChange={(e) => updateFormData('website', e.target.value)}
                style={{ fontSize: '16px', padding: '16px', marginBottom: '8px' }}
              />
              {formData.website && !isValidUrl(formData.website) && (
                <p className="validation-error">Please enter a valid website URL</p>
              )}
            </div>
          </motion.div>
        );

      // Screen 4: Primary Pain
      case 4:
        return (
          <motion.div key="pain" {...pageVariants} className="screen-content">
            <h1 className="question-title">Where do you lose most time or money?</h1>
            <p className="question-subtitle">Select the area that impacts your business the most</p>
            <div className="options-grid">
              {primaryPains.map(option => (
                <OptionCard
                  key={option.id}
                  option={option}
                  selected={formData.painPoint === option.id}
                  onClick={(id) => updateFormData('painPoint', id)}
                  showInput={option.hasInput && formData.painPoint === option.id}
                  inputValue={formData.painPointOther}
                  onInputChange={(val) => updateFormData('painPointOther', val)}
                />
              ))}
            </div>
          </motion.div>
        );

      // Screen 5: Business Description
      case 5:
        return (
          <motion.div key="description" {...pageVariants} className="screen-content">
            <h1 className="question-title">Tell us about your business</h1>
            <p className="question-subtitle">Describe your business and current challenges in 3-4 lines</p>
            <div className="form-field">
              <textarea
                className="form-textarea"
                placeholder="Briefly describe your business, what you do, and the main problems you face with current processes..."
                value={formData.businessDescription}
                onChange={(e) => updateFormData('businessDescription', e.target.value)}
                rows={5}
                style={{ fontSize: '16px', padding: '16px', marginBottom: '8px', minHeight: '120px', resize: 'vertical' }}
              />
              <div className="char-count">
                {getWordCount(formData.businessDescription)}/100 words (minimum 10 words required)
                {getWordCount(formData.businessDescription) < 10 && formData.businessDescription.length > 0 && (
                  <span className="validation-error"> - Please provide at least 10 words</span>
                )}
              </div>
            </div>
          </motion.div>
        );

      // Screen 6: Tool Recommendations
      case 6:
        const isShowingAllTools = formData.industry === 'other' || formData.painPoint === 'other';
        return (
          <motion.div key="tools" {...pageVariants} className="screen-content">
            <h1 className="question-title">
              {isShowingAllTools ? 'Browse our AI tools' : 'These tools are right for you'}
            </h1>
            <p className="question-subtitle">Tap to select the ones you're interested in</p>
            <div className="tools-grid">
              {recommendedTools.map((tool, index) => (
                <ToolCard
                  key={tool.id}
                  tool={tool}
                  selected={formData.selectedTools.includes(tool.id)}
                  onClick={(id) => {
                    const current = formData.selectedTools;
                    if (current.includes(id)) {
                      updateFormData('selectedTools', current.filter(t => t !== id));
                    } else {
                      updateFormData('selectedTools', [...current, id]);
                    }
                  }}
                  showBadge={!isShowingAllTools && index === 0}
                />
              ))}
            </div>
          </motion.div>
        );

      // Screen 7: Budget
      case 7:
        return (
          <motion.div key="budget" {...pageVariants} className="screen-content">
            <h1 className="question-title">What's your monthly budget for automation?</h1>
            <p className="question-subtitle">This helps us recommend the right package for you</p>
            <div className="options-grid">
              {budgetOptions.map(option => (
                <OptionCard
                  key={option.id}
                  option={option}
                  selected={formData.budget === option.id}
                  onClick={(id) => updateFormData('budget', id)}
                />
              ))}
            </div>
          </motion.div>
        );

      // Screen 8: Readiness
      case 8:
        return (
          <motion.div key="readiness" {...pageVariants} className="screen-content">
            <h1 className="question-title">Ready to implement within 30 days?</h1>
            <p className="question-subtitle">Let us know how we can best help you</p>
            <div className="options-grid">
              {readinessOptions.map(option => (
                <OptionCard
                  key={option.id}
                  option={option}
                  selected={formData.readiness === option.id}
                  onClick={(id) => updateFormData('readiness', id)}
                />
              ))}
            </div>
          </motion.div>
        );

      // Screen 9: Contact & Schedule (or Exploring)
      case 9:
        if (formData.readiness === 'exploring') {
          return (
            <motion.div key="exploring" {...pageVariants} className="exploring-screen">
              <div className="exploring-icon">👀</div>
              <h1 className="success-title">Thanks for exploring!</h1>
              <p className="success-subtitle">
                We'll send you useful tips and updates about AI automation for your business.
              </p>
              <div className="form-field" style={{ width: '100%', maxWidth: 320 }}>
                <label className="form-label">Your WhatsApp Number</label>
                <div className="phone-input-container">
                  <select
                    className="country-select"
                    value={formData.countryCode}
                    onChange={(e) => updateFormData('countryCode', e.target.value)}
                  >
                    {countryCodes.map(c => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    className="phone-input"
                    placeholder="Phone number"
                    value={formData.phoneNumber}
                    onChange={(e) => updateFormData('phoneNumber', e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              </div>
            </motion.div>
          );
        }

        if (formData.readiness === 'whatsapp') {
          // Generate WhatsApp message with all details
          const selectedTools = recommendedTools
            .filter(tool => formData.selectedTools.includes(tool.id))
            .map(tool => tool.name)
            .join(', ') || 'No tools selected';

          const whatsappMessage = `Hi! I'm interested in AI automation for my business. Here are my details:

🏢 Business: ${formData.businessName || 'Not provided'}
🌐 Website: ${formData.website || 'Not provided'}
🏭 Industry: ${formData.industry === 'other' ? formData.industryOther : industries.find(i => i.id === formData.industry)?.label || 'Not specified'}
👥 Team Size: ${teamSizes.find(t => t.id === formData.teamSize)?.label || 'Not specified'}

📝 Business Description:
${formData.businessDescription || 'Not provided'}

⚡ Main Pain Point: ${formData.painPoint === 'other' ? formData.painPointOther : primaryPains.find(p => p.id === formData.painPoint)?.label || 'Not specified'}

🛠️ Interested Tools: ${selectedTools}
💰 Budget: ${budgetOptions.find(b => b.id === formData.budget)?.label || 'Not specified'}

📞 Contact: ${formData.name || 'Not provided'}
📧 Email: ${formData.email || 'Not provided'}
📱 Phone: ${formData.countryCode}${formData.phoneNumber}

Please let me know how we can proceed with implementing these solutions.`;

          const encodedMessage = encodeURIComponent(whatsappMessage);
          const whatsappUrl = `https://wa.me/918847229036?text=${encodedMessage}`;

          return (
            <motion.div key="whatsapp" {...pageVariants} className="exploring-screen">
              <div className="exploring-icon">💬</div>
              <h1 className="success-title">Share Your Details</h1>
              <p className="success-subtitle">
                Click below to send your business details via WhatsApp. We'll review and get back to you within 2 hours.
              </p>
              
              <div className="form-field" style={{ width: '100%', maxWidth: 320, marginBottom: '20px' }}>
                <label className="form-label">Your Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                />
              </div>

              <div className="form-field" style={{ width: '100%', maxWidth: 320, marginBottom: '20px' }}>
                <label className="form-label">Email (optional)</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={(e) => updateFormData('email', e.target.value)}
                />
              </div>

              <div className="form-field" style={{ width: '100%', maxWidth: 320 }}>
                <label className="form-label">Your WhatsApp Number</label>
                <div className="phone-input-container">
                  <select
                    className="country-select"
                    value={formData.countryCode}
                    onChange={(e) => updateFormData('countryCode', e.target.value)}
                  >
                    {countryCodes.map(c => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    className="phone-input"
                    placeholder="Phone number"
                    value={formData.phoneNumber}
                    onChange={(e) => updateFormData('phoneNumber', e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              </div>

              {formData.name && formData.phoneNumber.length >= 10 && (
                <motion.a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="success-cta"
                  style={{ marginTop: '30px', display: 'inline-flex', alignItems: 'center', gap: '10px' }}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Send Details on WhatsApp
                </motion.a>
              )}
            </motion.div>
          );
        }

        return (
          <motion.div key="contact" {...pageVariants} className="screen-content">
            <h1 className="question-title">Book your Product Fit Call</h1>
            <p className="question-subtitle">30-minute call to understand your needs and demo the solution</p>
            
            <div className="form-field">
              <label className="form-label">Your Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter your name"
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
              />
            </div>

            <div className="form-field">
              <label className="form-label">Email (for calendar invite)</label>
              <input
                type="email"
                className="form-input"
                placeholder="Enter your email"
                value={formData.email}
                onChange={(e) => updateFormData('email', e.target.value)}
              />
            </div>

            <div className="form-field">
              <label className="form-label">WhatsApp Number</label>
              <div className="phone-input-container">
                <select
                  className="country-select"
                  value={formData.countryCode}
                  onChange={(e) => updateFormData('countryCode', e.target.value)}
                >
                  {countryCodes.map(c => (
                    <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  className="phone-input"
                  placeholder="Phone number"
                  value={formData.phoneNumber}
                  onChange={(e) => updateFormData('phoneNumber', e.target.value.replace(/\D/g, ''))}
                />
              </div>
            </div>

            <div className="date-picker-container">
              <label className="date-picker-label">Select Date</label>
              <div className="dates-scroll">
                {availableDates.map((dateObj, index) => (
                  <div
                    key={index}
                    className={`date-card ${formData.selectedDate?.toDateString() === dateObj.date.toDateString() ? 'selected' : ''}`}
                    onClick={() => {
                      updateFormData('selectedDate', dateObj.date);
                      updateFormData('selectedSlot', null);
                    }}
                  >
                    <div className="date-day">{dateObj.dayName}</div>
                    <div className="date-num">{dateObj.displayDate}</div>
                  </div>
                ))}
              </div>
            </div>

            {formData.selectedDate && (
              <div className="time-slots-container">
                <label className="date-picker-label">Select Time</label>
                {isLoadingSlots ? (
                  <div className="loading-container">
                    <div className="loading-spinner" />
                    <span className="loading-text">Loading available slots...</span>
                  </div>
                ) : (
                  <div className="time-slots-grid">
                    {availableSlots.map((slot, index) => (
                      <div
                        key={index}
                        className={`time-slot ${formData.selectedSlot?.start === slot.start ? 'selected' : ''}`}
                        onClick={() => updateFormData('selectedSlot', slot)}
                      >
                        {slot.displayTime}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        );

      // Screen 10: Success
      case 10:
        if (formData.readiness === 'exploring') {
          return (
            <motion.div key="success-exploring" {...pageVariants} className="success-screen">
              <div className="success-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h1 className="success-title">You're on the list!</h1>
              <p className="success-subtitle">
                We'll send you helpful AI automation tips and updates. Stay tuned!
              </p>
            </motion.div>
          );
        }

        if (formData.readiness === 'whatsapp') {
          return (
            <motion.div key="success-whatsapp" {...pageVariants} className="success-screen">
              <div className="success-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h1 className="success-title">Details Shared!</h1>
              <p className="success-subtitle">
                Thank you for sharing your business details. We'll review them and get back to you within 2 hours on WhatsApp with a tailored solution.
              </p>
              <a 
                href={`https://wa.me/918847229036`}
                className="success-cta"
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginTop: '20px' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Continue on WhatsApp
              </a>
            </motion.div>
          );
        }

        return (
          <motion.div key="success" {...pageVariants} className="success-screen">
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h1 className="success-title">Call Scheduled!</h1>
            <p className="success-subtitle">
              We've sent a calendar invite to your email. <br/>Looking forward to helping automate your business!
            </p>
            <p className="success-subtitle">
              Please accept the invite. Check spam folder if you don't see it in your inbox.
            </p>
            <div className="success-details">
              <div className="success-detail-row">
                <span className="success-detail-label">Date</span>
                <span className="success-detail-value">{formData.selectedDate?.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
              </div>
              <div className="success-detail-row">
                <span className="success-detail-label">Time</span>
                <span className="success-detail-value">{formData.selectedSlot?.displayTime}</span>
              </div>
              <div className="success-detail-row">
                <span className="success-detail-label">Duration</span>
                <span className="success-detail-value">30 minutes</span>
              </div>
            </div>
            <a 
              href={`https://wa.me/918847229036?text=Hi! I just booked a call for ${formData.selectedDate?.toLocaleDateString('en-IN')} at ${formData.selectedSlot?.displayTime}. Looking forward to it!`}
              className="success-cta"
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Connect on WhatsApp
            </a>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="funnel-container">
      {currentStep <= 9 && (
        <header className="funnel-header">
          <div className="logo-container">
            <img src="https://galific.com/assets/logo.png" alt="Galific" className="logo" />
            <span className="logo-tagline">Automate your business with AI</span>
          </div>
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
        </header>
      )}

      <AnimatePresence mode="wait">
        {renderScreen()}
      </AnimatePresence>

      {currentStep <= 9 && (
        <nav className="navigation">
          {currentStep > 1 && (
            <button className="nav-button secondary" onClick={handleBack}>
              ← Back
            </button>
          )}
          {currentStep < 9 ? (
            <button 
              className="nav-button primary" 
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Continue →
            </button>
          ) : (
            <button 
              className="nav-button primary" 
              onClick={handleSubmit}
              disabled={!canProceed() || isSubmitting}
            >
              {isSubmitting ? 'Booking...' : 'Book Call →'}
            </button>
          )}
        </nav>
      )}
    </div>
  );
}

export default App;
